import React, { useEffect, useState, useCallback } from "react";
import { KeyRound, RefreshCw, Copy, Check, Clock, X } from "lucide-react";
import { adminApi } from "../lib/api";

function ResetLinkModal({ item, onClose }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await adminApi.generateResetLink(item.id);
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur lors de la génération du lien.");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!result?.reset_url) return;
    try {
      await navigator.clipboard.writeText(result.reset_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers that block clipboard API
      const el = document.createElement("textarea");
      el.value = result.reset_url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const expiresLabel = result?.link_expires_at
    ? new Date(result.link_expires_at).toLocaleString()
    : "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-foreground/40 hover:text-foreground/70"
        >
          <X size={16} />
        </button>

        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-1">
          / réinitialisation
        </div>
        <h2 className="font-display text-xl font-semibold text-primary mb-1">
          Générer un lien de réinitialisation
        </h2>
        <p className="text-sm text-foreground/60 mb-5">
          Pour : <strong>{item.email}</strong>
          <br />
          Demande créée le : {new Date(item.created_at).toLocaleString()}
        </p>

        {error && (
          <div className="text-xs text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm mb-4">
            {error}
          </div>
        )}

        {result ? (
          <div className="space-y-3">
            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-sm p-3">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#166534] mb-2">
                Lien de réinitialisation (valable jusqu'à {expiresLabel})
              </div>
              <div className="font-mono text-xs text-[#166534] break-all leading-relaxed">
                {result.reset_url}
              </div>
            </div>
            <button
              onClick={copyLink}
              className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                copied
                  ? "bg-green-600 text-white"
                  : "bg-primary text-white hover:bg-primary-light"
              }`}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copié !" : "Copier le lien"}
            </button>
            <p className="text-xs text-foreground/50 text-center">
              Envoyez ce lien au subscriber via WhatsApp ou tout autre canal sécurisé.
              Il expire dans 1 heure.
            </p>
            <button
              onClick={generate}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm text-xs text-foreground/60 hover:bg-primary/5 hover:text-primary transition-colors"
            >
              <RefreshCw size={12} /> Regénérer un nouveau lien
            </button>
          </div>
        ) : (
          <button
            onClick={generate}
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white px-5 py-3 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-60"
          >
            {loading ? (
              <RefreshCw size={14} className="animate-spin" />
            ) : (
              <KeyRound size={14} />
            )}
            Générer le lien
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminPasswordResets() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await adminApi.listPasswordResetRequests({ pending_only: true });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleClose = () => {
    setSelected(null);
    load();
  };

  return (
    <div data-testid="admin-password-resets">
      {selected && <ResetLinkModal item={selected} onClose={handleClose} />}

      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">
          / abonnés
        </div>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">
              Réinitialisations de mot de passe
            </h1>
            <p className="text-sm text-foreground/60 mt-1">
              Demandes en attente d'un lien de réinitialisation — cliquez sur
              "Envoyer un lien" pour en générer un à transmettre manuellement.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-primary transition-colors"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Actualiser
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm">
          {error}
        </div>
      )}

      <div className="bg-white border border-primary/15 rounded-sm overflow-hidden">
        {loading ? (
          <div className="px-5 py-10 text-center text-sm text-foreground/50">
            Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <KeyRound size={32} className="mx-auto text-foreground/20 mb-3" />
            <p className="text-sm text-foreground/50">
              Aucune demande de réinitialisation en attente.
            </p>
          </div>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-primary/10 bg-primary/3">
              <span className="font-mono text-[10px] uppercase tracking-wider text-foreground/50">
                {total} demande{total !== 1 ? "s" : ""} en attente
              </span>
            </div>
            <ul>
              {items.map((item) => (
                <li
                  key={item.id}
                  className="px-5 py-4 border-b border-primary/5 last:border-none hover:bg-primary/3 transition-colors"
                >
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-medium text-sm text-foreground/90 truncate">
                        {item.email}
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="inline-flex items-center gap-1 font-mono text-[10px] text-foreground/50">
                          <Clock size={10} />
                          {new Date(item.created_at).toLocaleString()}
                        </span>
                        {item.link_generated_at && (
                          <span className="font-mono text-[10px] text-[#b4551c] bg-accent/10 px-1.5 py-0.5 rounded-sm">
                            Lien envoyé le {new Date(item.link_generated_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setSelected(item)}
                      className="shrink-0 inline-flex items-center gap-2 bg-primary text-white px-3 py-2 rounded-sm text-xs font-medium hover:bg-primary-light transition-colors"
                    >
                      <KeyRound size={12} />
                      {item.link_generated_at ? "Renvoyer un lien" : "Envoyer un lien"}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
